import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { rolesApi } from "../../api/rolesApi";
import { toast } from "../../components/common/toast";
import { getApiErrorMessage } from "../../utils/apiError";
import { blockedAction } from "../../components/common/blockedAction";
import i18n from "../../i18n";

export const fetchRoles = createAsyncThunk(
  "roles/fetchAll",
  async (payload: any, { getState }) => {
    const state = getState() as { roles: RolesState };

    const { isActive } = state.roles.searchParams;
    const organisationId = localStorage.getItem("organisation");

    // A lookup by id (the role page, opened fresh from the URL) must find the
    // role whatever its state, not just what the list's saved filter shows:
    // an inactive or deleted role's page otherwise came back empty on reload.
    const byIds = Array.isArray(payload?.ids) && payload.ids.length > 0;
    const requestBody = {
      pageSize: 100,
      pageIndex: 0,
      orderBy: payload?.orderBy || "string",
      // Read server-side as "ascending" (same quirk as the contract
      // taxonomy lists); only consulted when orderBy names a real field.
      sortDecending: payload?.sortAscending ?? false,
      isActive: byIds ? null : isActive,
      ids: payload.roleName || [],
      ...(byIds ? { includeDeleted: true } : {}),
      ...payload,
      // Always scoped to the current (switched) organisation — placed after
      // the payload spread so no caller-supplied filter can override it.
      organisationIds: [organisationId],
    };

    const response = await rolesApi.searchRoles(requestBody);
    return {
      data: response.data,
      count: response.count,
      pageIndex: requestBody.pageIndex,
    };
  },
);

export const createNewRole = createAsyncThunk(
  "roles/create",
  async (data: any, { rejectWithValue }) => {
    try {
      return await rolesApi.createRole(data);
    } catch (err) {
      // Carry the server's reason (e.g. duplicate role name) to the caller.
      return rejectWithValue(
        getApiErrorMessage(
          err,
          i18n.t("roles:sliceMessages.addError", {
            defaultValue: "Failed to create role",
          }),
        ),
      );
    }
  },
);

export const updateExistingRole = createAsyncThunk(
  "roles/update",
  async (
    data: {
      id: string;
      roleName: string;
      allowedPermissions: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      return await rolesApi.updateRole(data);
    } catch (err) {
      return rejectWithValue(
        getApiErrorMessage(
          err,
          i18n.t("roles:sliceMessages.editError", {
            defaultValue: "Failed to update role",
          }),
        ),
      );
    }
  },
);

export const handleActivateRole = createAsyncThunk(
  "roles/activate",
  async (id: string) => {
    await rolesApi.activateRole(id);
    return id;
  },
);

export const handleDeactivateRole = createAsyncThunk(
  "roles/deactivate",
  async (id: string) => {
    await rolesApi.deActivateRole(id);
    return id;
  },
);

export const publishRole = createAsyncThunk(
  "roles/publish",
  async (id: string, { rejectWithValue }) => {
    try {
      await rolesApi.publishRole(id);
      return id;
    } catch (err) {
      return rejectWithValue(
        getApiErrorMessage(
          err,
          i18n.t("roles:sliceMessages.publishError", {
            defaultValue: "Failed to publish role",
          }),
        ),
      );
    }
  },
);

export const revertRole = createAsyncThunk(
  "roles/revert",
  async (id: string, { rejectWithValue }) => {
    try {
      await rolesApi.revertRole(id);
      return id;
    } catch (err) {
      // e.g. "assigned to 3 staff in 2 other organisations" — carried as is.
      return rejectWithValue(
        getApiErrorMessage(
          err,
          i18n.t("roles:sliceMessages.revertError", {
            defaultValue: "Failed to revert role",
          }),
        ),
      );
    }
  },
);

export const removeRole = createAsyncThunk(
  "roles/remove",
  async (id: string, { rejectWithValue }) => {
    try {
      await rolesApi.removeRole(id);
      return id;
    } catch (err) {
      // The API refuses to delete a role that active staff still hold and
      // names them — carry that reason to the caller.
      return rejectWithValue(
        getApiErrorMessage(
          err,
          i18n.t("roles:sliceMessages.removeError", {
            defaultValue: "Failed to remove role",
          }),
        ),
      );
    }
  },
);

export const fetchPermissionsTemplate = createAsyncThunk(
  "roles/fetchTemplate",
  async () => {
    const response = await rolesApi.getDisplayTemplate();
    return response.permission;
  },
);

interface RolesState {
  items: any[];
  permissionsTemplate: any[];
  total: number;
  currentPage: number;
  isLoading: boolean;
  deactivateLoading: boolean;
  error: string | null;
  searchParams: {
    isActive: boolean | null;
    ids: string[];
  };
}

const initialState: RolesState = {
  items: [],
  permissionsTemplate: [],
  total: 0,
  currentPage: 0,
  isLoading: false,
  deactivateLoading: false,
  error: null,
  searchParams: {
    isActive: null,
    ids: [],
  },
};

const rolesSlice = createSlice({
  name: "roles",
  initialState,
  reducers: {
    resetRolesList: (state) => {
      state.items = [];
      state.currentPage = 0;
    },
    setRolesSearchParams: (
      state,
      action: { payload: { isActive: boolean | null; ids: string[] } },
    ) => {
      state.searchParams = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch cases
      .addCase(fetchRoles.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.pageIndex === 0) {
          state.items = action.payload.data;
        } else {
          state.items = [...state.items, ...action.payload.data];
        }
        state.total = action.payload.count;
        state.currentPage = action.payload.pageIndex;
      })
      .addCase(fetchRoles.rejected, (state) => {
        state.isLoading = false;
        toast.error(
          i18n.t("roles:sliceMessages.fetchError", {
            defaultValue: "Failed to fetch roles",
          }),
        );
      })

      // Fetch template
      .addCase(fetchPermissionsTemplate.fulfilled, (state, action) => {
        state.permissionsTemplate = action.payload || [];
      })

      // Create case
      .addCase(createNewRole.fulfilled, (state, action) => {
        // /Role/create only returns the new id, not the full entity — build
        // a minimal record from what was actually submitted so the role is
        // visible in the list immediately, without waiting on a re-fetch
        // (whose sort order might otherwise bury it past the first page).
        const newId =
          typeof action.payload === "string"
            ? action.payload
            : (action.payload as { id?: string } | undefined)?.id;
        if (newId && !state.items.some((r) => r.id === newId)) {
          state.items = [
            {
              id: newId,
              roleName: action.meta.arg.roleName,
              allowedPermissions: action.meta.arg.allowedPermissions,
              isActive: true,
              isPublish: false,
              creationDate: new Date().toISOString(),
              modificationDate: new Date().toISOString(),
            },
            ...state.items,
          ];
          state.total += 1;
        }
        toast.success(
          i18n.t("roles:sliceMessages.addSuccess", {
            defaultValue: "Role created successfully",
          }),
        );
      })
      .addCase(createNewRole.rejected, (_state, action) => {
        toast.error(
          typeof action.payload === "string" && action.payload
            ? action.payload
            : i18n.t("roles:sliceMessages.addError", {
                defaultValue: "Failed to create role",
              }),
        );
      })

      // Edit case
      .addCase(updateExistingRole.fulfilled, (state, action) => {
        const index = state.items.findIndex((r) => r.id === action.meta.arg.id);
        if (index !== -1) {
          state.items[index] = { ...state.items[index], ...action.meta.arg };
        }
        toast.success(
          i18n.t("roles:sliceMessages.editSuccess", {
            defaultValue: "Role updated successfully",
          }),
        );
      })
      .addCase(updateExistingRole.rejected, (_state, action) => {
        toast.error(
          typeof action.payload === "string" && action.payload
            ? action.payload
            : i18n.t("roles:sliceMessages.editError", {
                defaultValue: "Failed to update role",
              }),
        );
      })

      // Activate case
      .addCase(handleActivateRole.fulfilled, (state, action) => {
        const role = state.items.find((r) => r.id === action.payload);
        if (role) role.isActive = true;
        toast.success(
          i18n.t("roles:sliceMessages.activateSuccess", {
            defaultValue: "Role activated successfully",
          }),
        );
      })
      .addCase(handleActivateRole.rejected, () => {
        toast.error(
          i18n.t("roles:sliceMessages.activateError", {
            defaultValue: "Failed to activate role",
          }),
        );
      })

      // Deactivate case
      .addCase(handleDeactivateRole.fulfilled, (state, action) => {
        const role = state.items.find((r) => r.id === action.payload);
        if (role) role.isActive = false;
        toast.success(
          i18n.t("roles:sliceMessages.deactivateSuccess", {
            defaultValue: "Role deactivated successfully",
          }),
        );
      })
      .addCase(handleDeactivateRole.rejected, () => {
        toast.error(
          i18n.t("roles:sliceMessages.deactivateError", {
            defaultValue: "Failed to deactivate role",
          }),
        );
      })

      // Publish case
      .addCase(publishRole.fulfilled, (state, action) => {
        const role = state.items.find((r) => r.id === action.payload);
        if (role) role.isPublish = true;
        toast.success(
          i18n.t("roles:sliceMessages.publishSuccess", {
            defaultValue: "Role published successfully",
          }),
        );
      })
      .addCase(publishRole.rejected, (_state, action) => {
        toast.error(
          typeof action.payload === "string" && action.payload
            ? action.payload
            : i18n.t("roles:sliceMessages.publishError", {
                defaultValue: "Failed to publish role",
              }),
        );
      })

      // Revert case
      .addCase(revertRole.fulfilled, (state, action) => {
        const role = state.items.find((r) => r.id === action.payload);
        if (role) role.isPublish = false;
        toast.success(
          i18n.t("roles:sliceMessages.revertSuccess", {
            defaultValue: "Role reverted successfully",
          }),
        );
      })
      .addCase(revertRole.rejected, (_state, action) => {
        const fallback = i18n.t("roles:sliceMessages.revertError", {
          defaultValue: "Failed to revert role",
        });
        const message =
          typeof action.payload === "string" && action.payload
            ? action.payload
            : fallback;
        if (message === fallback) toast.error(fallback);
        else blockedAction.show({ message });
      })

      // Remove case
      .addCase(removeRole.fulfilled, (state, action) => {
        state.items = state.items.filter((r) => r.id !== action.payload);
        toast.success(
          i18n.t("roles:sliceMessages.removeSuccess", {
            defaultValue: "Role removed successfully",
          }),
        );
      })
      .addCase(removeRole.rejected, (_state, action) => {
        const fallback = i18n.t("roles:sliceMessages.removeError", {
          defaultValue: "Failed to remove role",
        });
        const message =
          typeof action.payload === "string" && action.payload
            ? action.payload
            : fallback;
        if (message === fallback) toast.error(fallback);
        else blockedAction.show({ message });
      });
  },
});

export const { resetRolesList, setRolesSearchParams } = rolesSlice.actions;
export default rolesSlice.reducer;
